import { isFlight, isNotIncludedService } from '@fcom/common-booking/utils/common-booking.utils';

import { FinnairServiceItemWithUpsell } from '../interfaces';
import {
  Category,
  FinnairBoundItem,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairTravelEndpoint,
} from '../api/models';

const getFlightOD = (flight: { arrival: FinnairTravelEndpoint; departure: FinnairTravelEndpoint }) =>
  `${flight.departure.locationCode}-${flight.arrival.locationCode}`;

const isInfant = (passenger: FinnairPassengerItem) => passenger.passengerTypeCode === FinnairPassengerCode.INF;

export const getSeatsByFlight = (
  passengers: FinnairPassengerItem[],
  currentBound: FinnairBoundItem,
  serviceItem: FinnairServiceItemWithUpsell
): Map<string, string[]> => {
  const flights = new Map<string, string[]>();
  const dashes = '--';
  currentBound.itinerary
    .filter(isFlight)
    .flatMap((flight) => {
      flights.set(getFlightOD(flight), []);
      return passengers.filter((passenger) => !isInfant(passenger)).map((passenger) => ({ flight, passenger }));
    })
    .forEach(({ flight, passenger }) => {
      const seatForPassengerInFlight = serviceItem.bounds
        .filter((bound) => bound.id === currentBound.id)
        .flatMap((bound) => bound.segments)
        .filter((serviceSegment) => serviceSegment.id === flight.id)
        .flatMap((serviceSegment) => serviceSegment.passengers)
        .filter((servicePax) => servicePax.id === passenger.id)
        .flatMap((servicePax) => servicePax.services)
        .flatMap((servicePaxService) => servicePaxService.seatNumber)
        .filter((seatNumber) => seatNumber !== null && seatNumber.length > 0);
      if (seatForPassengerInFlight?.length > 0) {
        const seatNumber = seatForPassengerInFlight[0];
        flights.get(getFlightOD(flight)).push(seatNumber);
      } else {
        flights.get(getFlightOD(flight)).push(dashes);
      }
    });

  return Array.from(flights.values()).every((seats: []) => seats.every((s) => s === dashes))
    ? new Map<string, string[]>()
    : flights;
};

const EXCLUDE_FROM_SUMMARY_VARIANTS = ['SH_ECO_MEAL_DEFAULT_1', 'SH_ECO_MEAL_DEFAULT_2', 'SH_ECO_DRINK_DEFAULT_1'];

const EXCLUDE_FROM_SUMMARY_CABIN_BAGGAGE_VARIANTS = [
  'CABIN_WEIGHT',
  'CABIN_WEIGHT_COMBINED_OLD',
  'CABIN_WEIGHT_COMBINED',
];

export const getSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell
): { quantity?: number; description: string } => {
  if (service.category === Category.OTHER) {
    return { description: 'MMB.services.other' };
  }
  const { anyIncluded, totalQuantity } = calculateIsIncludedAndQuantity(service, currentBound);

  const serviceCategory = resolveCategory(service.category);

  if (totalQuantity > 0) {
    return { quantity: totalQuantity, description: `MMB.services.${serviceCategory}.paid` };
  } else if (anyIncluded) {
    if ([Category.SPORT, Category.LOUNGE, Category.WIFI].includes(service.category) && totalQuantity === 0) {
      return { quantity: 1, description: `MMB.services.${serviceCategory}.included` };
    }

    return { description: `MMB.services.${serviceCategory}.included` };
  }
  return { description: `MMB.services.${serviceCategory}.noneIncluded` };
};

export const getMealSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  isShortHaulFlight: boolean
): { quantity?: number; description: string } => {
  const { anyIncluded, totalQuantity } = calculateIsIncludedAndQuantity(service, currentBound);

  const serviceCategory = resolveCategory(service.category);

  if (totalQuantity > 0) {
    return { quantity: totalQuantity, description: `MMB.services.${serviceCategory}.paid` };
  }

  if (anyIncluded && !isShortHaulFlight) {
    return { description: `MMB.services.${serviceCategory}.included` };
  }
  return { description: `MMB.services.${serviceCategory}.noneIncluded` };
};

function calculateIsIncludedAndQuantity(
  service: FinnairServiceItemWithUpsell,
  currentBound: FinnairBoundItem
): { anyIncluded: boolean; totalQuantity: number } {
  const allSelectedItems = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .flatMap((pax) => pax.services)
    .filter(({ variant }) => !EXCLUDE_FROM_SUMMARY_VARIANTS.includes(variant));

  const totalQuantity = allSelectedItems
    .filter((paxService) => isNotIncludedService(paxService))
    .flatMap((paxService) => paxService.quantity)
    .reduce((total, count) => {
      total += count;
      return total;
    }, 0);

  const anyIncluded = allSelectedItems.some(
    (paxService) => paxService.includedInTicketType || paxService.includedInTierBenefit
  );

  return { anyIncluded, totalQuantity };
}

export const getCabinBaggageSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell,
  passengers: FinnairPassengerItem[]
): { quantity?: number; description: string }[] => {
  const sortedPassengerOrder = passengers.flatMap((p) => p.id);
  const gropedByVariant = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .sort((a, b) => sortedPassengerOrder.indexOf(a.id) - sortedPassengerOrder.indexOf(b.id))
    .flatMap((pax) => pax.services)
    .filter((s) => !EXCLUDE_FROM_SUMMARY_CABIN_BAGGAGE_VARIANTS.includes(s.variant))
    .reduce((acc, cur) => {
      (acc[cur.variant] = acc[cur.variant] || []).push(cur);

      return acc;
    }, {});

  return Object.keys(gropedByVariant).map((key) => {
    const totalQuantity = gropedByVariant[key]
      .flatMap((paxService) => paxService.quantity)
      .reduce((total, count) => {
        total += count;
        return total;
      }, 0);

    const serviceVariant = key.toLowerCase();

    if (totalQuantity) {
      return {
        quantity: totalQuantity,
        description: `MMB.services.cabinBaggage.amount.${serviceVariant}`,
      };
    }

    return { description: `MMB.services.cabinBaggage.${serviceVariant}` };
  });
};

const getPaxServiceDisplayName = (paxService: FinnairPassengerServiceSelectionItem): string => {
  return paxService.parameters?.baggageWeight > 0
    ? paxService.displayName.replace('{weight}', '' + paxService.parameters.baggageWeight)
    : paxService.displayName;
};

export const getCheckedBaggageSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell
): { quantity?: number; description: string }[] => {
  const countsByFlightByVariant = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .map((flight) => {
      //for each flight:
      const countsInFlight = flight.passengers
        .flatMap((pax) => pax.services)
        .flatMap((paxService) => ({
          description: getPaxServiceDisplayName(paxService),
          quantity: paxService.quantity,
        }));
      return countsInFlight.reduce((countsInFlightByVariant, countItem) => {
        let countForVariant = countsInFlightByVariant.get(countItem.description) || 0;
        countForVariant += countItem.quantity;
        countsInFlightByVariant.set(countItem.description, countForVariant);
        return countsInFlightByVariant;
      }, new Map<string, number>());
    });
  //max count per flight and subCategory
  const maxCountByVariant = countsByFlightByVariant.reduce((perVariant, flightCount) => {
    //find max
    flightCount.forEach((count, variant) => {
      const maxCount = perVariant.get(variant) || 0;
      perVariant.set(variant, Math.max(maxCount, count));
    });
    return perVariant;
  }, new Map<string, number>());
  const summaryItems = [];
  maxCountByVariant.forEach((countInBound, variant) => {
    summaryItems.push({ description: variant, quantity: countInBound });
  });
  return summaryItems;
};

export const resolveCategory = (category: Category): string => {
  switch (category) {
    case Category.MEAL:
      return 'meals';
    case Category.BAGGAGE:
      return 'bags';
    default:
      return category;
  }
};
